/**
 * Reads booking-tool deep-link params from the URL.
 * Supports the legacy kebab-case keys used by the old booking tool
 * (e.g. `booking-id`, `location-code`, `screen=timeslot`) plus camelCase aliases.
 */

export type BookingToolUrlParams = {
  language?: string
  /**
   * Two-letter country code (e.g. `nl`). Pre-selects the store locator's
   * country dropdown and sets the analytics country. Distinct from
   * `location-id` / `location-code`, which pick a specific store and skip the
   * locator entirely.
   */
  location?: string
  locationId?: string
  locationCode?: string
  employeeId?: string
  bookingId?: string
  serviceId?: string
  /** CRM customer id — with `signature`, prefills the user-data form. */
  userId?: string
  /** Bearer-style token for the authorized customer lookup. */
  signature?: string
  /**
   * Derived from `screen`:
   * - `timeslot` / `timeslots` → reschedule
   * - `cancel` → cancel confirmation
   */
  initialScreen?: 'timeslots' | 'cancel'
}

function firstParam(
  params: URLSearchParams,
  ...keys: string[]
): string | undefined {
  for (const key of keys) {
    const value = params.get(key)
    if (value) return value
  }
  return undefined
}

function resolveInitialScreen(
  screen: string | undefined,
): BookingToolUrlParams['initialScreen'] {
  if (!screen) return undefined
  const normalized = screen.toLowerCase()
  if (normalized === 'cancel') return 'cancel'
  if (normalized === 'timeslot' || normalized === 'timeslots')
    return 'timeslots'
  return undefined
}

/**
 * Restores `+` characters in a base64 signature.
 *
 * A signature is minted with `+` in it, and any link that reaches us without it
 * percent-encoded has already had those turned into spaces by query parsing.
 * The old tool applied the same repair — without it every authorized customer
 * lookup 401s.
 */
function restoreSignature(value: string | undefined): string | undefined {
  return value?.replace(/\s/g, '+')
}

export function getBookingToolUrlParams(
  search: string = typeof window !== 'undefined' ? window.location.search : '',
): BookingToolUrlParams {
  const params = new URLSearchParams(search)
  const screen = firstParam(params, 'screen')

  return {
    language: firstParam(params, 'language'),
    // Exact-match lookup, so it never collides with `location-id` /
    // `location-code` — the old tool accepted the same bare `location` key.
    location: firstParam(params, 'location'),
    locationId: firstParam(params, 'location-id', 'locationId'),
    locationCode: firstParam(params, 'location-code', 'locationCode'),
    employeeId: firstParam(params, 'employee-id', 'employeeId'),
    bookingId: firstParam(params, 'booking-id', 'bookingId'),
    serviceId: firstParam(params, 'service-id', 'serviceId'),
    userId: firstParam(params, 'user-id', 'userId'),
    signature: restoreSignature(firstParam(params, 'signature')),
    initialScreen: resolveInitialScreen(screen),
  }
}
